// The four things a new workspace has to do, and the words the product uses for
// them (ADR-0016).
//
// This file is copy and structure only. It performs no query, reads no state and
// imports nothing — which is what lets the wording be rewritten, translated or
// white-labelled without touching a line of behaviour.

export type MilestoneId = "connect" | "first-post" | "schedule" | "library";

export interface Milestone {
  id: MilestoneId;
  /** Shown as "Step 2 of 4". */
  step: number;
  /** The instruction, in the imperative. Used everywhere the step is named. */
  title: string;
  /** Past tense, for a step already done. "Connect an account" is an odd label
   *  to put a tick against. */
  doneTitle: string;
  /** The half-line under a completed step. Warmth, not information. */
  doneNote: string;
  /** One line, for a step that is waiting its turn. */
  short: string;
  /** The line under the step being asked for right now. Says what it buys them,
   *  where `short` only says what it is. */
  nudge: string;
  /** The paragraph in the drawer: what this step is and why it comes here. */
  lede: string;
  /** Two or three facts that answer the objection this step usually raises. */
  points: string[];
  /** Where the step is actually performed. */
  route: string;
  /** The button that goes there. */
  cta: string;
  /** Without this, the step is somebody else's job (ADR-0011). */
  permission: string;
  /** What a member who lacks that permission is told instead. */
  delegated: string;
}

export const MILESTONES: Milestone[] = [
  {
    id: "connect",
    step: 1,
    title: "Connect an account",
    doneTitle: "Account connected",
    doneNote: "You're all set",
    short: "Connect Facebook, Instagram or YouTube",
    nudge: "Sign in on Facebook or Google and pick the accounts we may publish to.",
    lede:
      "Nothing can be published until there is somewhere to publish it. Connecting happens on " +
      "Facebook's and Google's own sites — you sign in there, choose which Pages, Instagram " +
      "professional accounts and YouTube channels to include, and come back.",
    points: [
      "Your social password stays with Facebook or Google. We never see it and cannot ask for it.",
      "What we receive is an access token you can revoke at any time, from their side or ours.",
      "Connecting Facebook also picks up any Instagram professional account linked to your Pages.",
    ],
    route: "/connections",
    cta: "Connect an account",
    permission: "connection.connect",
    delegated:
      "Connecting an account is an owner or admin action. Once someone has connected one, you " +
      "can publish to it without ever holding the credentials.",
  },
  {
    id: "first-post",
    step: 2,
    title: "Create your first post",
    doneTitle: "First post created",
    doneNote: "Great start!",
    short: "Write a caption, attach media, pick destinations",
    nudge: "One caption, one file, every account it should reach.",
    lede:
      "Write the caption once, attach a photo or a video, and tick every account it should go " +
      "to. Each destination is prepared separately, so one caption can reach three networks " +
      "without three trips.",
    points: [
      "The file is checked before anything is queued — type, size, dimensions, aspect ratio and duration, against each destination's own rules.",
      "A problem is shown next to the destination it affects, before you publish rather than after.",
      "YouTube appears for video, Facebook and Instagram for whatever they accept.",
    ],
    route: "/compose",
    cta: "Compose a post",
    permission: "post.create",
    delegated:
      "Creating posts is not part of your role here. You will still see everything that goes " +
      "out, and what happened to it at each destination.",
  },
  {
    id: "schedule",
    step: 3,
    title: "Schedule one for later",
    doneTitle: "Post scheduled",
    doneNote: "Running on its own",
    short: "Pick a date and time instead of publishing now",
    nudge: "Let us publish it at the time you chose, device off, browser closed.",
    lede:
      "The same post, with a release time on it. Scheduling runs on our servers, so the post " +
      "goes out at the time you chose whether or not your browser is open or your device is on.",
    points: [
      "Choose the time in the composer — everything else about the post works exactly the same.",
      "How far ahead you can schedule depends on your plan; the composer shows the limit.",
      "Approving, scheduling and publishing are separate permissions, so a schedule is not a way around review.",
    ],
    route: "/compose",
    cta: "Schedule a post",
    permission: "post.schedule",
    delegated:
      "Choosing the release time is a separate authority in this workspace, and it is not one " +
      "of yours. Submit the post and whoever holds it will time it.",
  },
  {
    id: "library",
    step: 4,
    title: "Upload for the future",
    doneTitle: "Media uploaded",
    doneNote: "Nice work!",
    short: "Build a library so the next post takes two minutes",
    nudge: "Stock the library now so next week's post starts already half made.",
    lede:
      "Put the photos and videos you already have into the media library now. The composer picks " +
      "from what is stored, so the posts you make next week do not start with an upload and a " +
      "wait.",
    points: [
      "Upload from this device, or import straight from a cloud drive without the file passing through it.",
      "Every asset remembers the posts it was used in, so you can see where a picture has already been.",
      "Storage is part of your plan; the library shows how much of it you have used.",
    ],
    route: "/media",
    cta: "Open the media library",
    permission: "media.upload",
    delegated:
      "Uploading to the shared library is limited to the roles that manage media here. You can " +
      "still use everything already in it.",
  },
];

export const MILESTONE_COUNT = MILESTONES.length;

/**
 * What the coach says, given how much is left.
 *
 * Copy, so it lives here with the rest of it. It is deliberately short of
 * congratulation until there is something to congratulate: "Great progress!" at
 * zero steps done is the tone people learn to stop reading.
 */
export function coachLine(doneCount: number): string {
  const left = MILESTONE_COUNT - doneCount;
  if (left <= 0) return "Everything is set up. Go and publish something.";
  if (left === 1) return "You're one step away from publishing on autopilot.";
  if (doneCount === 0) return "Start with one account. The rest takes a few minutes.";
  return `${left} steps to go — each one takes a couple of minutes.`;
}
